import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/axiosConfig';
import { toast } from 'react-toastify';
import { ChartPieIcon, MagnifyingGlassIcon, PencilSquareIcon, CheckIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import PageLayout, { PageHeader, StatsGrid, Toolbar, TableCard } from '../../components/common/PageLayout';

export default function AdminLeaveBalances() {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchBalances = async () => {
    setLoading(true);
    try {
      const res = await api.get('/workforce/leaves/all-balances');
      setBalances(res.data);
    } catch (err) {
      toast.error("Failed to load leave balances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBalances(); }, []);

  const handleEditClick = (emp) => {
    setEditingId(emp.workforce_id);
    setEditForm({
      casual_total: emp.casual_total,
      sick_total: emp.sick_total,
      earned_total: emp.earned_total,
      unpaid_used: emp.unpaid_used
    });
  };

  const handleSaveEdit = async (workforceId) => {
    try {
      await api.put(`/workforce/leaves/balances/${workforceId}`, editForm);
      toast.success("Leave balance updated!");
      setEditingId(null);
      fetchBalances(); // Reload data
    } catch (err) {
      toast.error("Failed to update balances");
    }
  };

  const filteredBalances = useMemo(() => {
    return balances.filter(b => b.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [balances, searchTerm]);

  const statsList = useMemo(() => {
    const totalEmps = balances.length;
    const totalCasual = balances.reduce((acc, b) => acc + (b.casual_used || 0), 0);
    const totalSick = balances.reduce((acc, b) => acc + (b.sick_used || 0), 0);
    const totalUnpaid = balances.reduce((acc, b) => acc + (b.unpaid_used || 0), 0);
    return [
      { label: 'Total Employees', value: totalEmps, icon: ChartPieIcon, color: 'default' },
      { label: 'Casual Leaves Used', value: totalCasual, icon: ChartPieIcon, color: 'blue' },
      { label: 'Sick Leaves Used', value: totalSick, icon: ChartPieIcon, color: 'orange' },
      { label: 'Unpaid Absences', value: totalUnpaid, icon: ChartPieIcon, color: 'red' },
    ];
  }, [balances]);

  return (
    <PageLayout>
      
      {/* HEADER */}
      <PageHeader
        icon={ChartPieIcon}
        iconColor="text-[#E8612C]"
        title="Company Leave Balances"
        subtitle="Track paid leave quotas and monitor unpaid absences globally"
        onRefresh={fetchBalances}
        loading={loading}
      />

      {/* STATS */}
      <StatsGrid stats={statsList} cols={4} />

      {/* TOOLBAR */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search employee name or ID..."
      />

      {/* TABLE */}
      <TableCard>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-gray-50 dark:bg-[#161b22] text-gray-500 dark:text-slate-400 text-xs uppercase font-bold tracking-wide border-b border-gray-200 dark:border-[#30363d]">
              <tr>
                <th className="px-5 py-4">Employee Details</th>
                <th className="px-5 py-4 text-center border-l border-gray-200 dark:border-[#30363d]">Casual Leaves</th>
                <th className="px-5 py-4 text-center border-l border-gray-200 dark:border-[#30363d]">Sick Leaves</th>
                <th className="px-5 py-4 text-center border-l border-gray-200 dark:border-[#30363d]">Earned Leaves</th>
                <th className="px-5 py-4 text-center border-l border-gray-200 dark:border-[#30363d] bg-red-50/50 dark:bg-rose-950/20 text-red-700 dark:text-red-400">Unpaid Taken</th>
                <th className="px-5 py-4 text-center border-l border-gray-200 dark:border-[#30363d]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#30363d]">
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-400 dark:text-slate-500">Loading balances...</td></tr>
              ) : filteredBalances.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-400 dark:text-slate-500">No employees found.</td></tr>
              ) : (
                filteredBalances.map((emp) => {
                  const isEditing = editingId === emp.workforce_id;

                  return (
                    <tr key={emp.workforce_id} className="hover:bg-gray-50/80 dark:hover:bg-[#1f242c] transition">
                      <td className="px-5 py-4">
                        <p className="font-bold text-gray-900 dark:text-white">{emp.name}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">{emp.department} • {emp.employee_id}</p>
                      </td>

                      {/* CASUAL */}
                      <td className="px-5 py-4 text-center border-l border-gray-100 dark:border-[#30363d]">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs text-gray-400 dark:text-slate-500">Total:</span>
                            <input type="number" value={editForm.casual_total} onChange={e => setEditForm({...editForm, casual_total: e.target.value})} className="w-16 p-1 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] dark:text-white rounded text-center text-xs" />
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg font-bold text-gray-800 dark:text-slate-200">{emp.casual_total - emp.casual_used}</p>
                            <p className="text-[10px] text-gray-500 dark:text-slate-400">{emp.casual_used} used of {emp.casual_total}</p>
                          </div>
                        )}
                      </td>

                      {/* SICK */}
                      <td className="px-5 py-4 text-center border-l border-gray-100 dark:border-[#30363d]">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs text-gray-400 dark:text-slate-500">Total:</span>
                            <input type="number" value={editForm.sick_total} onChange={e => setEditForm({...editForm, sick_total: e.target.value})} className="w-16 p-1 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] dark:text-white rounded text-center text-xs" />
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg font-bold text-gray-800 dark:text-slate-200">{emp.sick_total - emp.sick_used}</p>
                            <p className="text-[10px] text-gray-500 dark:text-slate-400">{emp.sick_used} used of {emp.sick_total}</p>
                          </div>
                        )}
                      </td>

                      {/* EARNED */}
                      <td className="px-5 py-4 text-center border-l border-gray-100 dark:border-[#30363d]">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <span className="text-xs text-gray-400 dark:text-slate-500">Total:</span>
                            <input type="number" value={editForm.earned_total} onChange={e => setEditForm({...editForm, earned_total: e.target.value})} className="w-16 p-1 bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] dark:text-white rounded text-center text-xs" />
                          </div>
                        ) : (
                          <div>
                            <p className="text-lg font-bold text-gray-800 dark:text-slate-200">{emp.earned_total - emp.earned_used}</p>
                            <p className="text-[10px] text-gray-500 dark:text-slate-400">{emp.earned_used} used of {emp.earned_total}</p>
                          </div>
                        )}
                      </td>

                      {/* UNPAID */}
                      <td className="px-5 py-4 text-center border-l border-gray-100 dark:border-[#30363d] bg-red-50/30 dark:bg-rose-950/20">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <input type="number" step="0.5" value={editForm.unpaid_used} onChange={e => setEditForm({...editForm, unpaid_used: e.target.value})} className="w-16 p-1 bg-white dark:bg-[#0d1117] border border-red-300 dark:border-rose-800 rounded text-center text-xs font-bold text-red-600 dark:text-rose-400" />
                          </div>
                        ) : (
                          <p className="text-lg font-bold text-red-600 dark:text-rose-400">{emp.unpaid_used}</p>
                        )}
                      </td>

                      {/* ACTIONS */}
                      <td className="px-5 py-4 text-center border-l border-gray-100 dark:border-[#30363d]">
                        {isEditing ? (
                          <div className="flex justify-center gap-2">
                            <button onClick={() => handleSaveEdit(emp.workforce_id)} className="p-1.5 bg-green-100 dark:bg-emerald-950/60 text-green-700 dark:text-emerald-400 rounded-lg hover:bg-green-200 transition" title="Save"><CheckIcon className="w-4 h-4"/></button>
                            <button onClick={() => setEditingId(null)} className="p-1.5 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-200 transition" title="Cancel"><XMarkIcon className="w-4 h-4"/></button>
                          </div>
                        ) : (
                          <button onClick={() => handleEditClick(emp)} className="p-1.5 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition" title="Adjust Balance">
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </TableCard>
    </PageLayout>
  );
}